import type { Metadata, MetadataRoute } from "next";

import { publicSitePublicationFlags } from "@/lib/publication-flags";

const posthogApiHost =
  process.env.NEXT_PUBLIC_POSTHOG_PROXY_HOST ||
  process.env.NEXT_PUBLIC_POSTHOG_HOST ||
  "";
const posthogRuntimeAllowed =
  process.env.NODE_ENV === "production" &&
  (process.env.NEXT_PUBLIC_POSTHOG_ENV ?? process.env.NODE_ENV) ===
    "production";
const posthogReplaySampleRate = (() => {
  const raw = process.env.NEXT_PUBLIC_POSTHOG_REPLAY_SAMPLE_RATE;

  if (!raw) {
    return 0.2;
  }

  const parsed = Number(raw);

  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : 0.2;
})();

if (
  process.env.NODE_ENV === "production" &&
  process.env.NEXT_PUBLIC_POSTHOG_ENABLED === "true"
) {
  const trimmedProjectToken = (
    process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN ?? ""
  ).trim();
  const trimmedPostHogApiHost = posthogApiHost.trim();

  if (trimmedProjectToken === "") {
    throw new Error(
      "NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN must be configured when PostHog is enabled in production.",
    );
  }

  if (trimmedPostHogApiHost === "") {
    throw new Error(
      "NEXT_PUBLIC_POSTHOG_PROXY_HOST must be configured when PostHog is enabled in production.",
    );
  }

  if (!trimmedPostHogApiHost.startsWith("https://")) {
    throw new Error(
      "NEXT_PUBLIC_POSTHOG_PROXY_HOST must use https:// in production.",
    );
  }

  const posthogHostname = (() => {
    try {
      return new URL(trimmedPostHogApiHost).hostname.toLowerCase();
    } catch {
      return "";
    }
  })();

  if (posthogHostname === "" || posthogHostname.endsWith("posthog.com")) {
    throw new Error(
      "NEXT_PUBLIC_POSTHOG_PROXY_HOST must point to a first-party proxy host, not directly to PostHog.",
    );
  }
}

export const siteConfig = {
  name: "Cooperly",
  siteName: "Cooperly",
  url: process.env.NEXT_PUBLIC_SITE_URL ?? "https://cooperly.ai",
  description:
    "Cooperly turns the people context founders keep in their heads into a living operating layer, so ownership stays clear and managers know when to act.",
  ogImage: "/cooperly/og.png",
  ogImageWidth: 1200,
  ogImageHeight: 630,
  supportEmail: "support@cooperly.ai",
  privacyEmail: "privacy@cooperly.ai",
  googleAnalyticsId: "G-ZBTVRYN0YC",
  cookieYesClientId: "79366ebad7326add6ebf0af3de580fe0",
  posthog: {
    enabled:
      process.env.NEXT_PUBLIC_POSTHOG_ENABLED === "true" &&
      posthogRuntimeAllowed,
    projectToken: process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN?.trim() ?? "",
    apiHost: posthogApiHost,
    uiHost: process.env.NEXT_PUBLIC_POSTHOG_UI_HOST ?? "",
    instanceId: process.env.NEXT_PUBLIC_POSTHOG_INSTANCE_ID ?? "cooperly-main",
    environment: process.env.NEXT_PUBLIC_POSTHOG_ENV ?? process.env.NODE_ENV,
    captureExceptions:
      process.env.NEXT_PUBLIC_POSTHOG_CAPTURE_EXCEPTIONS === "true",
    captureConsoleErrors:
      process.env.NEXT_PUBLIC_POSTHOG_CAPTURE_CONSOLE_ERRORS === "true",
    autocaptureEnabled:
      process.env.NEXT_PUBLIC_POSTHOG_AUTOCAPTURE_ENABLED !== "false",
    replayEnabled: process.env.NEXT_PUBLIC_POSTHOG_REPLAY_ENABLED === "true",
    replaySampleRate: posthogReplaySampleRate,
    heatmapsEnabled:
      process.env.NEXT_PUBLIC_POSTHOG_HEATMAPS_ENABLED === "true",
    releaseVersion: process.env.NEXT_PUBLIC_POSTHOG_RELEASE_VERSION ?? "",
    runtimeAllowed: posthogRuntimeAllowed,
  },
} as const;

export const editorialAuthor = {
  name: "Ed Khristus",
  role: "Founder, Cooperly",
  url: process.env.NEXT_PUBLIC_SITE_URL ?? "https://cooperly.ai",
} as const;

export type SiteRoute = {
  id:
    | "home"
    | "manualLanding"
    | "product"
    | "compare"
    | "aiAgents"
    | "resources"
    | "integrations"
    | "guides"
    | "stories"
    | "insights"
    | "manifesto"
    | "roadmap"
    | "partnerProgram"
    | "privacyPolicy"
    | "termsConditions";
  path: string;
  title: string;
  description: string;
  keywords?: string[];
  ogImage?: string;
  indexable: boolean;
  sitemap: boolean;
  changeFrequency: MetadataRoute.Sitemap[number]["changeFrequency"];
  priority: number;
  lastModified: string;
};

export const routes = {
  home: {
    id: "home",
    path: "/",
    title: "Cooperly.ai | Human Operating System for Founder-Led Teams",
    description:
      "Cooperly turns the people context founders keep in their heads into a living operating layer, so ownership stays clear and managers know when to act.",
    keywords: [
      "human operating system",
      "founder-led team software",
      "team operating system",
      "people context software",
      "AI-native company",
    ],
    indexable: true,
    sitemap: true,
    changeFrequency: "weekly",
    priority: 1,
    lastModified: "2026-10-07T00:00:00.000Z",
  },
  manualLanding: {
    id: "manualLanding",
    path: "/create-a-manual-for-yourself",
    title: "Manual of Me Test & Examples | Cooperly",
    description:
      "Take Cooperly’s 6-minute Manual of Me test, explore 24 working profiles, and create a shareable How to Work With Me page for your team.",
    keywords: [
      "manual of me",
      "manual of me examples",
      "how to work with me",
      "personal user manual",
      "working style profile",
    ],
    indexable: true,
    sitemap: true,
    changeFrequency: "weekly",
    priority: 0.9,
    lastModified: "2026-10-01T00:00:00.000Z",
  },
  product: {
    id: "product",
    path: "/product",
    title: "Product | Team Context And Leadership Workflows | Cooperly",
    description:
      "Explore the Cooperly product: Overview, Coop Profile, Fundamentals, Pulse, Candidates, and Random Coffee in one calmer workflow for leaders.",
    keywords: [
      "cooperly product",
      "team context platform",
      "leadership workflow software",
      "team health product",
      "working style software",
    ],
    indexable: false,
    sitemap: false,
    changeFrequency: "weekly",
    priority: 0.92,
    lastModified: "2026-04-20T00:00:00.000Z",
  },
  compare: {
    id: "compare",
    path: "/compare",
    title: "Compare Team Management Tools for Growing Teams | Cooperly",
    description:
      "Compare Lattice, Workleap, Officevibe, Peoplebox, Fellow, Range, Geekbot, Steady and Windmill by the management habit each automates, and where it stops.",
    keywords: [
      "team management software comparison",
      "lattice alternatives",
      "async standup alternatives",
      "ai performance review tools",
      "one on one meeting software",
      "employee engagement tools",
      "human operating system",
    ],
    indexable: true,
    sitemap: true,
    changeFrequency: "monthly",
    priority: 0.82,
    lastModified: "2026-11-04T00:00:00.000Z",
  },
  aiAgents: {
    id: "aiAgents",
    path: "/ai-context-layer",
    title: "People Context Layer for AI Tools | Cooperly",
    description:
      "Give the same trusted context to the AI tools your team already uses through selected, permissioned access. AI changes wait for a person's confirmation.",
    keywords: [
      "people context layer",
      "team context for AI tools",
      "permissioned AI context",
      "cooperly ai context layer",
      "mcp people context",
      "human operating system",
    ],
    indexable: true,
    sitemap: true,
    changeFrequency: "monthly",
    priority: 0.84,
    lastModified: "2026-09-10T00:00:00.000Z",
  },
  resources: {
    id: "resources",
    path: "/resources",
    title: "Resources | Insights, Guides, And Stories | Cooperly",
    description:
      "Explore Cooperly resources across Insights, Cooperly 101 guides, and short customer stories for team leadership and People Ops.",
    keywords: [
      "team leadership resources",
      "people ops resources",
      "cooperly guides",
      "team health insights",
      "customer stories",
    ],
    indexable: publicSitePublicationFlags.learnHub,
    sitemap: publicSitePublicationFlags.learnHub,
    changeFrequency: "weekly",
    priority: 0.8,
    lastModified: "2026-04-20T00:00:00.000Z",
  },
  integrations: {
    id: "integrations",
    path: "/integrations",
    title: "Cooperly Integrations for AI and Team Workflows",
    description:
      "Connect communication channels, approved AI tools and project systems to Cooperly for prompts, permissioned people context and managed roadmap work.",
    keywords: [
      "cooperly integrations",
      "people context integrations",
      "team reminders integration",
      "workplace ai integrations",
      "linear agent integration",
      "clickup agent integration",
      "asana roadmap sync",
      "mcp team context",
    ],
    indexable: true,
    sitemap: true,
    changeFrequency: "monthly",
    priority: 0.78,
    lastModified: "2026-10-07T00:00:00.000Z",
  },
  slackIntegration: {
    id: "integrations",
    path: "/integrations/slack",
    title: "Slack Integration for Team Reminders and Lead Digests | Cooperly",
    description:
      "Connect Cooperly to Slack for account-level workspace install, member sync, personal DMs, reminders, digests, recommendations, and urgent alerts.",
    keywords: [
      "cooperly slack integration",
      "slack team reminders",
      "slack pulse reminders",
      "people ops slack integration",
    ],
    indexable: true,
    sitemap: false,
    changeFrequency: "monthly",
    priority: 0.7,
    lastModified: "2026-04-29T00:00:00.000Z",
  },
  guides: {
    id: "guides",
    path: "/guides",
    title: "Cooperly 101 | Guides For Managers And Team Leads",
    description:
      "Learn how to run your first Cooperly rhythm, prepare tougher conversations, and use people context before day one.",
    keywords: [
      "cooperly 101",
      "manager guides",
      "team lead guides",
      "people ops guides",
      "how to use cooperly",
    ],
    indexable: publicSitePublicationFlags.guides,
    sitemap: publicSitePublicationFlags.guides,
    changeFrequency: "weekly",
    priority: 0.78,
    lastModified: "2026-04-20T00:00:00.000Z",
  },
  stories: {
    id: "stories",
    path: "/stories",
    title: "Cooperly Stories | Short Customer Stories And Team Moments",
    description:
      "Watch and read short Cooperly stories about earlier intervention, cleaner partner rhythms, and better management conversations.",
    keywords: [
      "cooperly stories",
      "customer stories",
      "team leadership stories",
      "people ops stories",
    ],
    indexable: publicSitePublicationFlags.stories,
    sitemap: publicSitePublicationFlags.stories,
    changeFrequency: "monthly",
    priority: 0.74,
    lastModified: "2026-04-20T00:00:00.000Z",
  },
  insights: {
    id: "insights",
    path: "/insights",
    title: "Founder Leadership & Team Management Insights | Cooperly",
    description:
      "Evidence-based guides on founder bottlenecks, delegation, team handoffs, manager conversations, working styles and human coordination around AI.",
    keywords: [
      "founder bottleneck guides",
      "founder leadership",
      "team management guides",
      "delegation and ownership",
      "working styles at work",
      "AI team coordination",
    ],
    indexable: true,
    sitemap: true,
    changeFrequency: "weekly",
    priority: 0.75,
    lastModified: "2026-09-10T00:00:00.000Z",
  },
  manifesto: {
    id: "manifesto",
    path: "/manifesto",
    title: "Cooperly Manifesto: Scale Without Management Bureaucracy",
    description:
      "The principles behind Cooperly: scale team context before HR process, protect human judgement, and help AI-native companies grow without bureaucracy.",
    keywords: [
      "cooperly manifesto",
      "human operating system",
      "founder-led company",
      "team context",
      "AI-native company management",
    ],
    indexable: true,
    sitemap: true,
    changeFrequency: "monthly",
    priority: 0.72,
    lastModified: "2026-09-10T00:00:00.000Z",
  },
  roadmap: {
    id: "roadmap",
    path: "/roadmap",
    title: "Roadmap | Cooperly",
    description:
      "See what Cooperly has shipped and what is coming next across product, integrations, MCP and Slack.",
    keywords: [
      "cooperly roadmap",
      "product roadmap",
      "ai workflow roadmap",
      "human operating system roadmap",
    ],
    indexable: true,
    sitemap: true,
    changeFrequency: "monthly",
    priority: 0.66,
    lastModified: "2026-10-30T00:00:00.000Z",
  },
  partnerProgram: {
    id: "partnerProgram",
    path: "/partner-program",
    title: "Partner Program for Fractional COOs & Coaches | Cooperly",
    description:
      "Run Cooperly with founder-led clients as a fractional COO, People Ops leader, founder coach or leadership coach, with up to 30% revenue share.",
    keywords: [
      "fractional COO partner program",
      "fractional people ops partner",
      "founder coach partner program",
      "leadership coach software",
      "cooperly partners",
    ],
    ogImage: "/cooperly/pp.png",
    indexable: true,
    sitemap: true,
    changeFrequency: "monthly",
    priority: 0.68,
    lastModified: "2026-09-10T00:00:00.000Z",
  },
  privacyPolicy: {
    id: "privacyPolicy",
    path: "/privacy-policy",
    title: "Privacy Policy | Cooperly",
    description:
      "Learn how Cooperly processes personal data across the website, product, billing, integrations, cookies, retention, security, transfers and data rights.",
    indexable: false,
    sitemap: false,
    changeFrequency: "yearly",
    priority: 0.4,
    lastModified: "2026-05-06T00:00:00.000Z",
  },
  termsConditions: {
    id: "termsConditions",
    path: "/terms-conditions",
    title: "Terms & Conditions | Cooperly",
    description:
      "Review Cooperly’s service terms, customer responsibilities, AI output rules, integrations, trials, billing, liability limits and Data Processing Addendum.",
    indexable: false,
    sitemap: false,
    changeFrequency: "yearly",
    priority: 0.4,
    lastModified: "2026-05-06T00:00:00.000Z",
  },
} as const satisfies Record<string, SiteRoute>;

export const routeList = Object.values(routes);

export function absoluteUrl(path = "/") {
  return new URL(path, siteConfig.url).toString();
}

export function getRouteConfig(path: string) {
  return routeList.find((route) => route.path === path);
}

export function getPrimaryRouteById(id: SiteRoute["id"]) {
  return (
    routeList.find((route) => route.id === id && route.sitemap) ??
    routeList.find((route) => route.id === id)
  );
}

export function getPrimaryRoutesByIds(ids: readonly SiteRoute["id"][]) {
  return ids.flatMap((id) => {
    const route = getPrimaryRouteById(id);
    return route ? [route] : [];
  });
}

export function buildLegacyRobots(indexable: boolean): Metadata["robots"] {
  return {
    index: indexable,
    follow: true,
    googleBot: {
      index: indexable,
      follow: true,
      "max-image-preview": "large",
      "max-snippet": -1,
      "max-video-preview": -1,
    },
  };
}

export function buildPageMetadata(route: SiteRoute): Metadata {
  const image = absoluteUrl(route.ogImage ?? siteConfig.ogImage);

  return {
    title: {
      absolute: route.title,
    },
    description: route.description,
    keywords: route.keywords,
    authors: [editorialAuthor],
    creator: editorialAuthor.name,
    publisher: editorialAuthor.name,
    alternates: {
      canonical: route.path,
    },
    robots: buildLegacyRobots(route.indexable),
    openGraph: {
      title: route.title,
      description: route.description,
      url: absoluteUrl(route.path),
      siteName: siteConfig.siteName,
      type: "website",
      locale: "en_US",
      images: [
        {
          url: image,
          width: siteConfig.ogImageWidth,
          height: siteConfig.ogImageHeight,
          alt: route.title,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title: route.title,
      description: route.description,
      images: [image],
    },
  };
}
